import { Plus, Trash2 } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { EuroInput } from './EuroInput';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import {
  emptyTaxLine,
  TAX_MODES,
  TAX_TYPES,
  VAT_NATURES,
  type ExpenseForm,
  type TaxLineDraft,
} from './form-model';
import { NativeSelect } from './NativeSelect';
import { TotalsLine } from './TotalsLine';

/**
 * The tax lines in a sheet over the form (design §1.3): type × mode, rate with rate/base/tax, fixed
 * with the tax only, exempt with the nature and the base; the non-taxable part; «Aliquota incerta».
 * The totals line says by how much it is off while it does not add up.
 */
export function TaxLinesSheet({
  open,
  onOpenChange,
  form,
  onChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly form: ExpenseForm;
  readonly onChange: (next: Partial<ExpenseForm>) => void;
}) {
  const { t } = useTranslation('expenses');
  const uid = useId();

  const setLine = (index: number, patch: Partial<TaxLineDraft>): void => {
    onChange({
      taxLines: form.taxLines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[92vh] overflow-y-auto p-4 lg:inset-x-auto lg:right-6 lg:left-auto lg:w-[40rem]
          lg:rounded-t-xl lg:border-x"
      >
        <div className="flex items-center justify-between gap-2">
          <SheetTitle>{t('capture.taxSheet.title')}</SheetTitle>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('capture.taxSheet.done')}
          </Button>
        </div>
        <SheetDescription>{t('capture.taxSheet.blocked')}</SheetDescription>

        <ol className="space-y-3">
          {form.taxLines.map((line, index) => (
            <TaxLineCard
              key={line.key}
              uid={uid}
              line={line}
              index={index}
              onLine={(patch) => setLine(index, patch)}
              onRemove={() => onChange({ taxLines: form.taxLines.filter((_, i) => i !== index) })}
            />
          ))}
        </ol>
        <Button
          type="button"
          variant="outline"
          onClick={() => onChange({ taxLines: [...form.taxLines, emptyTaxLine()] })}
        >
          <Plus aria-hidden="true" />
          {t('capture.taxSheet.add')}
        </Button>
        <Labelled id={`${uid}-non-taxable`} label={t('capture.fields.nonTaxable')}>
          <EuroInput
            id={`${uid}-non-taxable`}
            value={form.nonTaxable}
            onChange={(e) => onChange({ nonTaxable: e.target.value })}
          />
        </Labelled>
        <TotalsLine form={form} />
      </SheetContent>
    </Sheet>
  );
}

function TaxLineCard({
  uid,
  line,
  index,
  onLine,
  onRemove,
}: {
  readonly uid: string;
  readonly line: TaxLineDraft;
  readonly index: number;
  readonly onLine: (patch: Partial<TaxLineDraft>) => void;
  readonly onRemove: () => void;
}) {
  const { t } = useTranslation('expenses');
  const id = `${uid}-${line.key}`;
  const n = index + 1;
  return (
    <li className="space-y-2 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{t('capture.taxSheet.line', { n })}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('capture.taxSheet.remove', { n })}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </div>
      <TaxLineFields id={id} line={line} onLine={onLine} />
      {line.mode === 'rate' && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={line.rateUnclear}
            onChange={(e) => onLine({ rateUnclear: e.target.checked })}
          />
          {t('capture.taxSheet.rateUnclear')}
        </label>
      )}
    </li>
  );
}

function TaxLineFields({
  id,
  line,
  onLine,
}: {
  readonly id: string;
  readonly line: TaxLineDraft;
  readonly onLine: (patch: Partial<TaxLineDraft>) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div className="grid grid-cols-2 gap-2">
      <Labelled id={`${id}-type`} label={t('capture.taxSheet.type')}>
        <NativeSelect
          id={`${id}-type`}
          value={line.type}
          onChange={(e) => onLine({ type: e.target.value as TaxLineDraft['type'] })}
        >
          {TAX_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`capture.taxTypes.${type}`)}
            </option>
          ))}
        </NativeSelect>
      </Labelled>
      <Labelled id={`${id}-mode`} label={t('capture.taxSheet.mode')}>
        <NativeSelect
          id={`${id}-mode`}
          value={line.mode}
          onChange={(e) => onLine({ mode: e.target.value as TaxLineDraft['mode'] })}
        >
          {TAX_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {t(`capture.taxModes.${mode}`)}
            </option>
          ))}
        </NativeSelect>
      </Labelled>
      {line.mode === 'rate' && (
        <Labelled id={`${id}-rate`} label={t('capture.taxSheet.rate')}>
          <Input
            id={`${id}-rate`}
            inputMode="decimal"
            value={line.rate}
            onChange={(e) => onLine({ rate: e.target.value })}
          />
        </Labelled>
      )}
      {line.mode === 'exempt' && <NatureField id={id} line={line} onLine={onLine} />}
      {line.mode !== 'fixed' && (
        <Labelled id={`${id}-base`} label={t('capture.taxSheet.base')}>
          <EuroInput
            id={`${id}-base`}
            value={line.base}
            onChange={(e) => onLine({ base: e.target.value })}
          />
        </Labelled>
      )}
      {line.mode !== 'exempt' && (
        <Labelled id={`${id}-tax`} label={t('capture.taxSheet.tax')}>
          <EuroInput
            id={`${id}-tax`}
            value={line.tax}
            onChange={(e) => onLine({ tax: e.target.value })}
          />
        </Labelled>
      )}
    </div>
  );
}

function NatureField({
  id,
  line,
  onLine,
}: {
  readonly id: string;
  readonly line: TaxLineDraft;
  readonly onLine: (patch: Partial<TaxLineDraft>) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <Labelled id={`${id}-nature`} label={t('capture.taxSheet.nature')}>
      <NativeSelect
        id={`${id}-nature`}
        value={line.nature}
        onChange={(e) => onLine({ nature: e.target.value })}
      >
        <option value="">{t('capture.fields.none')}</option>
        {VAT_NATURES.map((nature) => (
          <option key={nature} value={nature}>
            {t(`capture.natures.${nature}`)}
          </option>
        ))}
      </NativeSelect>
    </Labelled>
  );
}

function Labelled({
  id,
  label,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
